<template>
  <div class="app">
    <!-- Embedded, the host owns the sidebar and renders <titan-remote-nav>
         in its own frame; rendering ours too would show two sidebars. -->
    <cl-sidebar v-if="!embedded" v-model="isClosed">
      <template #company-logo="{ isClosed }">
        <div
          v-if="isRequestPending"
          class="tw-w-[154px] tw-h-[52px] tw-flex tw-justify-center tw-items-center"
        >
          <cl-spinner
            :loading-text="$t('loading')"
            :size="'small'"
            class="tw-mr-2"
          />
        </div>
        <t-logo
          v-else
          @click.prevent="redirectBasedOnRole"
          :logo-src="logoSrc"
          v-show="!isClosed"
        />
      </template>
      <template #dropdown-select="{ isClosed }">
        <!-- TODO: fix assertion customer as UserAccount -->
        <t-nav-dropdown
          icon="cb-icon-customers"
          data-test-id="navbar-select-customer"
          :loading="false"
          :loading-more="loadingMore"
          :is-closed="isClosed"
          :options="userAccountList ? userAccountList : []"
          :initial-selected="initialMsp ? initialMsp.uuid : undefined"
          data-clarity-mask="true"
          @reset-list="setDropDownList"
          @search="itemBeingSearched"
          @selected="(customer) => selectCustomer(customer as UserAccount)"
          @load-more="loadMore"
        />
      </template>
      <template #side-nav="{ isClosed: isSidebarCollapsed }">
        <t-side-nav
          v-if="!loadingSolutions"
          :nav-menu-set-up="mspNavMenuSetUp"
          :is-sidebar-collapsed="isSidebarCollapsed"
          :tier="tier"
        ></t-side-nav>
      </template>
      <template #info-section="{ isClosed }" v-if="isDefaultBranding">
        <img
          v-if="!isClosed"
          alt="CyberSentriq logo"
          :src="cyberSentriqLogoSrc"
        />
      </template>
    </cl-sidebar>

    <!-- Embedded: the host owns the sidebar frame, so the SAME nav this
         layout already computes is teleported into its outlet instead.
         Rebuilding it from the shared nav state would drop this layout's
         licence/solution filtering and show unlicensed products. -->
    <titan-remote-nav-outlet v-if="embedded" v-slot="{ collapsed }">
      <!-- `isSidebarCollapsed` above is cl-sidebar's #side-nav slot prop, which
           does not exist out here — embedded, that slot is never rendered. The
           host owns the sidebar, so its collapse state comes from the outlet. -->
      <t-side-nav
        v-if="!loadingSolutions"
        :nav-menu-set-up="mspNavMenuSetUp"
        :is-sidebar-collapsed="collapsed"
        :tier="tier"
      ></t-side-nav>
    </titan-remote-nav-outlet>
    <div class="app-content">
      <!-- Embedded, the host owns the app header. -->
      <t-upper-nav v-if="!embedded" />
      <main
        class="tw-overflow-y-aut tw-flex tw-grow tw-shrink-0 tw-flex-col tw-bg-primary-grey-25 tw-p-4"
        :class="{ 'max-lg:tw-ml-14': !embedded }"
      >
        <div class="tw-flex tw-w-full tw-flex-1 tw-flex-col">
          <read-only-role-banner :visible="isReadOnlyRoleVisible" level="msp" />
          <legacy-migration-banner
            :visible="isLegacyMigrationBannerVisible"
            :count="accountsNeedingMigration"
            :total="totalConnectedAccounts"
          />
          <t-page-header
            :product-links="productLinks"
            :user-links="userLinks"
            :data-test-id="'header-user-menu'"
          />
          <!-- key forces a child remount when orgUuid changes so the view loads
               fresh data. Embedded, the host drives navigation through the shared
               history and the view is otherwise reused across orgs. -->
          <router-view :key="routeOrgKey" />
        </div>
      </main>
      <cl-footer
        v-if="!embedded"
        :other-products-text="$t('other-products')"
        :try-now-text="$t('try-now')"
        :msp-name="getOrganisationBranding.name ?? undefined"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { isEmbedded } from "@/remote/contract";
import { ref, onBeforeMount, computed, onMounted, watch } from "vue";
import { useAuthenticationStore } from "@/store/authentication.store";
import { useRouter, useRoute } from "vue-router";
import type { ComputedRef, Ref } from "vue";
import { getMediaUrl } from "@/config/runtimeConfig";

import TPageHeader from "@/components/TPageHeader";
import TNavDropdown from "@/components/TNavDropdown";
import { TIER } from "@/constants";

import {
  usePrimaryNavItems,
  useEnabledSolutions,
  useRedirectBasedOnRole,
} from "@/composables";
import { UUID_SEGMENT_REGEX } from "@/constants/regex";
import { useGlobalOrganisationID } from "@/composables/useGlobalOrganisationID";
import { useFlag } from "@/composables/useFlags/useFlag";
import { NAV_ITEMS_MODE } from "@/constants";
import { ALL_SERVICES } from "@/utils/services";

import TSideNav from "@/components/TSideNav";
import TLogo from "@/components/TLogo/TLogo.vue";
import TUpperNav from "@/components/TUpperNav/TUpperNav.vue";
import ReadOnlyRoleBanner from "@/views/Common/ReadOnlyRoleBanner.vue";
import LegacyMigrationBanner from "@/views/Common/LegacyMigrationBanner.vue";
import {
  useRequestStatus,
  PENDING,
  READY,
} from "@/composables/useRequestStatus";
import { useI18n } from "vue-i18n";
import type { DataItem } from "@/components/TSideNav/types";
import type { ProductLink } from "@/components/TPageHeader/TPageHeader.types";
import { getAccountShortName } from "@/utils/getAccountShortName";
import { DEFAULT_BRANDING_NAME } from "@/modules/Settings/constants";
import { storeToRefs } from "pinia";
import { useSidebarStore } from "@/store/sidebar";
import { useSolutionsStore } from "@/store/solutions.store";
import { useLegacyMigrationStore } from "@/store/legacyMigration.store";
import { ADMIN, SUPER_ADMIN } from "@/authorisation/authorisation.types";
import { useDropDownStore, type DropDownRequest } from "@/store/dropDown.store";
import { useBrandingStore } from "@/store/branding.store";
import { usePageMetaStore } from "@/store/pageMeta.store";
import { selectCustomerHandler } from "./selectCustomerHandler";
import { isNavItemDisabled } from "@/utils/navItemHelpers";
import TitanRemoteNavOutlet from "@/remote/TitanRemoteNavOutlet.vue";

// Snapshotted once at boot by the contract module.
const embedded = isEmbedded();
import type {
  NavItem,
  UserAccount,
  UserLink,
  AuthUser,
  SelectedAccount,
  SelectedMsp,
  EnabledSolution,
  Account,
} from "./MspLayout.types";

const authStore = useAuthenticationStore();
const pageMetaStore = usePageMetaStore();
const router = useRouter();
const route = useRoute();

const { getNavItems } = usePrimaryNavItems();
const { enabledSolutions } = useEnabledSolutions();
const { mspGlobalOrganisationId } = useGlobalOrganisationID();

const routeOrgKey = computed(() => {
  const param = route.params.orgUuid;
  return (Array.isArray(param) ? param[0] : param) || "msp-root";
});
const redirectBasedOnRole = useRedirectBasedOnRole();
const { setRequestStatus, isRequestPending } = useRequestStatus();

// Reactive, flag-filtered nav items: gated entries appear once the Harness SDK
// resolves their flag, and late module registrations flow through too.
const mspNavItems = getNavItems(NAV_ITEMS_MODE.MSP);
const loadingSolutions: Ref<boolean> = ref(true);

const scopeMspId = (id: string, orgUuid?: string) => {
  if (!orgUuid || !id?.startsWith("/msp")) return id;
  const segments = id.split("/").filter(Boolean);
  if (segments[0] !== "msp") return id;
  const remainder = segments.slice(1);
  if (remainder[0] && UUID_SEGMENT_REGEX.test(remainder[0])) {
    remainder.shift();
  }
  const suffix = remainder.length ? `/${remainder.join("/")}` : "";
  return `/msp/${orgUuid}${suffix}`;
};

const getSolutions = async (uuid: string): Promise<void> => {
  await solutionsStore.getSolutions({
    uuid,
  });
};

const getLegacyMigrationStatus = async (uuid: string): Promise<void> => {
  // The whole legacy-migration flow is gated behind M365_AZURE_APP_MIGRATION;
  // with the flag off, skip the fetch entirely so the banner stays hidden and
  // we don't hit the endpoint.
  if (!isAppMigrationEnabled.value) return;
  try {
    await legacyMigrationStore.fetchLegacyMigrationStatus({
      uuid,
    });
  } catch {
    // Non-critical: if the status can't be fetched, the banner just won't show.
  }
};

const selectedMsp: ComputedRef<SelectedMsp | null> = computed(
  () => authStore.selectedMsp as SelectedMsp | null,
);
const selectedAccount: ComputedRef<SelectedAccount | null> = computed(
  () => authStore.selectedAccount as SelectedAccount | null,
);

const isDefaultBranding = computed<boolean>(() => {
  return !!(
    !getOrganisationBranding.value.logo_dark &&
    getOrganisationBranding.value.name === DEFAULT_BRANDING_NAME
  );
});

onMounted(async () => {
  setRequestStatus({ type: PENDING });
  await brandingStore.getBranding({
    organisationUuid: selectedMsp?.value?.uuid || selectedAccount?.value?.uuid,
  });
  setRequestStatus({ type: READY });
});

onBeforeMount(async () => {
  authStore.setCustomer(null);
  const orgUuid = route.params?.orgUuid as string | undefined;
  if (orgUuid) {
    await authStore.setMspUser({
      uuid: orgUuid,
      name: selectedMsp.value?.name || "",
    });
    pageMetaStore.setMSP(selectedMsp.value?.name || "");
  }
  if (!mspGlobalOrganisationId.value) {
    return;
  }

  try {
    loadingSolutions.value = true;

    solutionsStore.resetState();
    legacyMigrationStore.resetState();

    await Promise.all([
      getSolutions(mspGlobalOrganisationId.value),
      getLegacyMigrationStatus(mspGlobalOrganisationId.value),
    ]);
  } finally {
    if (route.meta.solutionId) {
      let isSolutionDisabled = false;

      isSolutionDisabled = !enabledSolutions.value.some(
        (solution: EnabledSolution) =>
          solution.solution_uuid === route.meta.solutionId,
      );

      if (isSolutionDisabled) {
        await router.replace({
          name: "msp-root",
          params: { orgUuid: mspGlobalOrganisationId.value },
        });
        // Embedded, `router.go(0)` reloads the host's whole document — we share its history.
        if (!embedded) {
          router.go(0);
        }
      }
    }

    loadingSolutions.value = false;
  }
});

watch(
  () => route.params.orgUuid,
  async (orgUuid) => {
    if (orgUuid && orgUuid !== selectedMsp.value?.uuid) {
      await authStore.setMspUser({
        uuid: orgUuid as string,
        name: selectedMsp.value?.name || "",
      });
      pageMetaStore.setMSP(selectedMsp.value?.name || "");
    }
  },
  { immediate: true },
);

const userAccountList: Ref<UserAccount[] | null> = ref(null);
const initialMsp: Ref<SelectedMsp | null> = ref(null);
const tier = TIER.msps;
const loadingMore = ref(false);
const currentPage = ref(1);
const currentFilter = ref("");
const productLinks: Ref<ProductLink[]> = ref([]);
const userLinks: Ref<UserLink[]> = ref([]);

const { t } = useI18n();
const sidebarStore = useSidebarStore();
const { isClosed } = storeToRefs(sidebarStore);
const solutionsStore = useSolutionsStore();
const dropDownStore = useDropDownStore();
const brandingStore = useBrandingStore();
const legacyMigrationStore = useLegacyMigrationStore();
const { organisationBranding: getOrganisationBranding } =
  storeToRefs(brandingStore);
const { accountsNeedingMigration, totalConnectedAccounts } =
  storeToRefs(legacyMigrationStore);

const authUser: ComputedRef<AuthUser> = computed(
  () => authStore.authUser as AuthUser,
);
const dropDownList: ComputedRef<Account[]> = computed(
  () => dropDownStore.list as unknown as Account[],
);
const dropDownTotal = computed<number>(() => dropDownStore.total);
const hasMore = computed(() => dropDownList.value.length < dropDownTotal.value);
const setParentPath = (path: string) => pageMetaStore.setParentPath(path);
const setMSP = (msp: string) => pageMetaStore.setMSP(msp);
const setCustomer = (customer: unknown) =>
  authStore.setCustomer(
    customer as Parameters<typeof authStore.setCustomer>[0],
  );
const getDropDownList = (request: DropDownRequest) =>
  dropDownStore.getDropDownList(request);
const resetState = () => dropDownStore.resetState();

const isReadOnlyRoleVisible: ComputedRef<boolean> = computed(() => {
  return authUser.value?.role === "admin-ro";
});

const isMspAdminVisible: ComputedRef<boolean> = computed(() => {
  return authUser.value?.role === ADMIN || authUser.value?.role === SUPER_ADMIN;
});

const { isFeatureAvailable: isAppMigrationEnabled } = useFlag(
  "M365_AZURE_APP_MIGRATION",
);

const isLegacyMigrationBannerVisible: ComputedRef<boolean> = computed(
  () =>
    isAppMigrationEnabled.value &&
    isMspAdminVisible.value &&
    accountsNeedingMigration.value > 0,
);

const separatedByGroup = (mspArray: DataItem[]) => {
  const mspByGroup: Record<string, DataItem[]> = {};
  mspArray.forEach((item) => {
    if (mspByGroup[item.groupBy]) {
      mspByGroup[item.groupBy].push(item);
    } else {
      mspByGroup[item.groupBy] = [item];
    }
  });
  return mspByGroup;
};

const mspNavMenuSetUp: ComputedRef<Record<string, DataItem[]>> = computed(
  () => {
    const orgUuid =
      (route.params?.orgUuid as string) ||
      selectedMsp.value?.uuid ||
      selectedAccount.value?.uuid;

    const items = mspNavItems.value
      .map((item: NavItem) => {
        const serviceSettings = ALL_SERVICES.find(
          (service: { uuid: string }) => service.uuid === item.solutionId,
        );

        const isSolutionDisabled = ref(false);
        let tooltip: { content: string; placement: string } | undefined =
          undefined;

        if (item.solutionId) {
          isSolutionDisabled.value = !enabledSolutions.value.some(
            (solution: EnabledSolution) =>
              solution.service.uuid === item.solutionId,
          );
        }

        // Instead of hiding, the disabled services will appear for MSPs:
        // https://gitlab.titanhq.com/titanhq/dev/scrum/team-platfarmers/-/issues/2097
        if (isSolutionDisabled.value && serviceSettings) {
          tooltip = {
            content: t(serviceSettings.tooltipLabelUnavailable),
            placement: "top",
          };
        }
        return {
          ...item,
          id: scopeMspId(item.id, orgUuid),
          tooltip,
          disabled: isSolutionDisabled.value,
          children: item.children
            ?.filter(
              (child: NavItem) =>
                !isNavItemDisabled({
                  navItem: child,
                  enabledSolutions: enabledSolutions.value,
                  parentDisabled: isSolutionDisabled.value,
                  mspGlobalOrganisationId: mspGlobalOrganisationId.value,
                }),
            )
            .map((child: NavItem) => {
              return {
                ...child,
                id: scopeMspId(child.id, orgUuid),
                disabled: isSolutionDisabled.value,
                children: child.children?.filter(
                  (grandChild: NavItem) =>
                    !isNavItemDisabled({
                      navItem: grandChild,
                      enabledSolutions: enabledSolutions.value,
                      parentDisabled: false,
                      mspGlobalOrganisationId: mspGlobalOrganisationId.value,
                    }),
                ),
              };
            }),
        };
      })
      .filter(Boolean);

    // TODO: fix type assertion as DataItem[]
    return separatedByGroup(items as unknown as DataItem[]);
  },
);

const logoSrc: ComputedRef<string> = computed(() => {
  if (getOrganisationBranding.value?.logo_dark) {
    return `${getMediaUrl()}/${getOrganisationBranding.value.logo_dark}`;
  }
  return new URL("@/assets/branding/Titan_Logo.svg", import.meta.url).href;
});

const cyberSentriqLogoSrc = new URL(
  "@/assets/branding/powered-by-cybersentriq.svg",
  import.meta.url,
).href;

const selectCustomer = async (customer: UserAccount): Promise<void> => {
  await selectCustomerHandler({
    customer,
    selectedAccount: selectedAccount.value,
    setCustomer,
    router,
    routePath: route.path,
  });
};

const setDropDownList = async (): Promise<void> => {
  currentPage.value = 1;
  currentFilter.value = "";
  try {
    resetState();
    await getDropDownList({
      globalOrganisationID:
        selectedMsp.value?.uuid || selectedAccount.value?.uuid,
      params: {
        filter: "",
        results: 40,
        page: 1,
        sortkey: "name",
        sortdir: "asc",
      },
    });
  } finally {
    userAccountList.value = cleanAccounts(dropDownList.value);
  }
};

const itemBeingSearched = async (item: string): Promise<void> => {
  currentPage.value = 1;
  currentFilter.value = item;
  try {
    resetState();
    await getDropDownList({
      globalOrganisationID:
        selectedMsp.value?.uuid || selectedAccount.value?.uuid,
      params: {
        filter: item,
        results: 40,
        page: 1,
        sortkey: "name",
        sortdir: "asc",
      },
    });
  } catch {
    // Handled in axios interceptor
  }
};

const loadMore = async (): Promise<void> => {
  if (loadingMore.value || !hasMore.value) return;
  loadingMore.value = true;
  currentPage.value++;
  try {
    await dropDownStore.appendDropDownList({
      globalOrganisationID:
        selectedMsp.value?.uuid || selectedAccount.value?.uuid,
      params: {
        filter: currentFilter.value,
        results: 40,
        page: currentPage.value,
        sortkey: "name",
        sortdir: "asc",
      },
    });
  } finally {
    loadingMore.value = false;
  }
};

const cleanAccounts = (accounts: Account[]): UserAccount[] => {
  const allAccounts = accounts
    .map((acct: Account) => {
      return {
        ...acct,
        id: acct.uuid,
        name: acct.name || "",
        short: getAccountShortName(acct.name || ""),
        active: false,
        allCustomers: false,
        placeholder: "Search Customers",
      };
    })
    .filter(
      (res: Account) =>
        res.organisation_type === "Customer" ||
        res.organisation_type === "Distributor",
    );

  if (selectedAccount.value?.type_name === "System") {
    const isViewingChildMsp = Boolean(selectedMsp.value?.uuid);
    const otherAccounts = [
      {
        uuid: "",
        id: "",
        name: isViewingChildMsp ? t("all-customers-menu-item") : t("all-msps"),
        short: "AM",
        active: false,
        allMsps: !isViewingChildMsp,
        allCustomers: isViewingChildMsp,
        placeholder: isViewingChildMsp ? "Search Customers" : "Search MSPs",
        organisation_type: isViewingChildMsp ? "Customer" : "MSP",
      },
    ];
    return [...otherAccounts, ...allAccounts];
  } else {
    let otherAccounts = [
      {
        uuid: "",
        id: "",
        name: "All Customers",
        short: "AM",
        active: false,
        allMsps: false,
        allCustomers: true,
        placeholder: "Search Customers",
        organisation_type: "Customer",
      },
    ];
    return [...otherAccounts, ...allAccounts];
  }
};

watch(
  dropDownList,
  () => {
    userAccountList.value = cleanAccounts(dropDownList.value);
  },
  { deep: true, immediate: true },
);

watch(selectedMsp, async (newMsp, oldMsp) => {
  if (newMsp && newMsp.uuid !== oldMsp?.uuid) {
    loadingSolutions.value = true;
    solutionsStore.resetState();
    // Clear the previous MSP's migration counts up front: getLegacyMigrationStatus
    // swallows fetch errors, so without this the banner would keep showing the
    // old MSP's numbers if the new fetch fails.
    legacyMigrationStore.resetState();
    await Promise.all([
      getSolutions(newMsp.uuid),
      getLegacyMigrationStatus(newMsp.uuid),
    ]);
    loadingSolutions.value = false;
  }
});

const initializeLayout = (): void => {
  let parentPath = "";

  if (selectedAccount.value?.type_name === "System") {
    parentPath += "/system";
  }

  setParentPath(parentPath);
  setMSP(selectedMsp.value?.name || "");
};

// TODO route guard for route.meta.visibleForUuids

onBeforeMount(initializeLayout);
</script>
